import { Textarea } from "@/components/ui/textarea";
import { useIsFinancialManager } from "@/hooks/useIsFinancialManager";
import { useReadProjectGroupsScenariosById } from "@/libs/react-query/hooks/useScenarioQuery";
import ApprovalDataTable from "@/pages/Approval/components/ApprovalDataTable";
import { useBudgetAmountMap } from "@/pages/Project/Scenario/hooks/useBudgetAmountMap";
import { calculateScenarioBidSummary } from "@/pages/Project/Scenario/utils";
import useUserStore from "@/stores/User";
import { ReadScenarioApprovalsByIdResponse } from "@/types/scenario-approvals";
import { CurrentUserContent } from "@/types/setting";
import { Divide } from "lucide-react";
import { useMemo } from "react";

type Props = {
  data: ReadScenarioApprovalsByIdResponse;
};

export default function ApprovalDetailTable({ data: approval }: Props) {
  const storedUser: CurrentUserContent = useUserStore((state) => state.user);
  const isFinancialManager = useIsFinancialManager();

  const projectId = approval.project_id || "";
  const { budgetMap } = useBudgetAmountMap([projectId]);

  const { data } = useReadProjectGroupsScenariosById(
    approval.project_group_id || "",
    approval.scenario_id || ""
  );

  const convertedData = useMemo(() => {
    if (!data?.scenario) return null;

    return calculateScenarioBidSummary({
      scenarioData: data.scenario.data,
      budgetAmountMap: budgetMap[projectId] || {
        budget: 0,
        basisAmount: undefined,
      },
    });
  }, [data, budgetMap, projectId]);

  // 내가 결재할 수 있는 투찰률 및 재무 데이터 접근 권한 확인
  const { canViewMarginRate, canViewFullFinancialData, isApprover } =
    useMemo(() => {
      const { stages = [] } = approval;

      const isApprover = stages.some((stage) =>
        stage.to_list.some((entry) => entry.user.user_id === storedUser.user_id)
      );

      const isCcUser = stages.some((stage) =>
        stage.cc_list.some((entry) => entry.user.user_id === storedUser.user_id)
      );

      const canViewMarginRate = isApprover || isFinancialManager;
      const canViewFullFinancialData = isApprover || isFinancialManager;

      return { canViewMarginRate, canViewFullFinancialData, isApprover };
    }, [approval.stages]);

  if (!convertedData) {
    return null;
  }

  return (
    <div className="p-5 flex items-center gap-5">
      <div className="flex-1 flex flex-col gap-4">
        <div className="shadow-lg">
          <ApprovalDataTable
            data={convertedData.groupList}
            canViewMarginRate={canViewMarginRate}
            canViewFullFinancialData={canViewFullFinancialData}
            isApprover={isApprover}
          />
        </div>

        <div className="flex items-center gap-1 text-sm font-semibold">
          <span className="text-gray-B">투찰률</span>
          <span className="text-dark-red">
            <span className="text-dark-red">
              {convertedData.bidRate.toFixed(2)}% =
            </span>
          </span>
          <span className="text-gray-C"> 투찰 금액</span>
          <span className="text-gray-A">
            <span className="text-gray-A">
              {convertedData.bidAmount.toLocaleString()}
            </span>
          </span>
          <Divide className="text-gray-B" size={12} />
          <span className="text-gray-C">
            {convertedData.baseAmountMap.basisAmount
              ? "기초 금액"
              : "배정 예산"}
          </span>
          <span className="text-gray-A">
            <span className="text-gray-A">
              {(
                convertedData.baseAmountMap.basisAmount ||
                convertedData.baseAmountMap.budget
              ).toLocaleString()}
            </span>
          </span>
        </div>
      </div>
      <div className="flex-[0.5] flex flex-col gap-2">
        <p className="text-sm text-gray-A font-semibold">요청 사유</p>
        <Textarea
          value={approval?.description}
          className="resize-none h-60"
          readOnly
        />
      </div>
    </div>
  );
}
